import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import {
  defaultOrganizationsState,
  getInitialOrganizationState,
  OrganizationRestSet,
} from './organization-slice-definitions';
import {
  createSubOrganization,
  createUser,
  deleteOrganizationById,
  getOrganizationById,
  getAllOrganizationsById,
  patchOrganizationById,
  patchUserById,
  deleteUserById,
  getDeploymentWeeksById,
  getSearchResults,
  getLocationSetById,
  getOrganizationDeleteDataById,
  getOrganizationUsersById,
  getUserById,
  getAllUserDataById,
  getApiKeysByOrganizationId,
  createApiKey,
  deleteApiKeyById,
} from './organization-actions';
import { updateDataList } from '../../utils/generic-data-list-handlers';
import RestSet from '@common/interfaces/api/rest-set';
import { OrgUserRestSet } from '../../models/misc/api/rest-set';
import UserRole from '../../models/enums/user-roles';
import OrganizationRole from '../../models/enums/organization-roles';
import WebSocketConnectionStatus from '../../models/enums/websocket-connection-status';
import { removeToken } from '../../services/data/axios-base';

const initialOrganizationState = getInitialOrganizationState();

const organizationSlice = createSlice({
  name: 'organization',
  initialState: initialOrganizationState,
  reducers: {
    resetOrganizationState() {
      return { ...initialOrganizationState };
    },
    resetOrganizationsRestState(state, action: PayloadAction<Partial<OrganizationRestSet>>) {
      state.organizations.rest = { ...state.organizations.rest, ...action.payload };
    },
    resetUsersRestState(state, action: PayloadAction<Partial<OrgUserRestSet>>) {
      state.users.rest = { ...state.users.rest, ...action.payload };
    },
    resetSearchRestStatus(state) {
      state.search = { data: [], count: 0, getStatus: HttpFetchStatus.Idle, getError: null };
    },
    resetExternalApiRestState(state, action: PayloadAction<Partial<RestSet>>) {
      state.externalApi.rest = { ...state.externalApi.rest, ...action.payload };
    },
    clearCreatedApiKey(state) {
      state.externalApi.created = null;
    },
    setSelectedUserId(state, action: PayloadAction<string | null>) {
      state.selectedUserId = action.payload;
      if (action.payload != null) {
        state.users.current = null;
        state.users.rest.getByIdStatus = HttpFetchStatus.Idle;
        state.users.rest.getByIdError = null;
      }
    },
    setOrganizationWebSocketStatus(state, action: PayloadAction<WebSocketConnectionStatus>) {
      state.organizationHubStatus = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getOrganizationById.pending, (state, action) => {
      state.organizations.rest.getStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(getOrganizationById.fulfilled, (state, action) => {
      const locationSetReset = !state.organizations.locationSet?.some(
        (org) => org.id === action.payload.id,
      );
      return {
        ...initialOrganizationState,
        organizations: {
          rest: {
            ...defaultOrganizationsState.rest,
            getStatus: HttpFetchStatus.Success,
            patchCostCalculationsError: null,
            patchCostCalculationsStatus: HttpFetchStatus.Idle,
            getDeploymentWeeksStatus: HttpFetchStatus.Idle,
            getDeploymentWeeksError: null,
            getDeleteStatus: HttpFetchStatus.Idle,
            getDeleteError: null,
            getLocationSetStatus: locationSetReset
              ? HttpFetchStatus.Idle
              : state.organizations.rest.getLocationSetStatus,
            getLocationSetError: locationSetReset
              ? null
              : state.organizations.rest.getLocationSetError,
          },
          current: action.payload ?? null,
          locationSet: locationSetReset ? null : state.organizations.locationSet,
          sub: null,
          deploymentWeeks: null,
          deleteData: null,
        },
      };
    });
    builder.addCase(getOrganizationById.rejected, (state, action) => {
      state.organizations.rest.getStatus = HttpFetchStatus.Failed;
      state.organizations.rest.getError = action.payload ?? null;
    });
    builder.addCase(getAllOrganizationsById.pending, (state, action) => {
      state.organizations.rest.getAllOrganizationsStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(getAllOrganizationsById.fulfilled, (state, action) => {
      state.organizations.rest.getAllOrganizationsStatus = HttpFetchStatus.Success;
      state.organizations.sub = action.payload ?? null;
    });
    builder.addCase(getAllOrganizationsById.rejected, (state, action) => {
      state.organizations.rest.getAllOrganizationsStatus = HttpFetchStatus.Failed;
      state.organizations.rest.getAllOrganizationsError = action.payload ?? null;
    });
    builder.addCase(getLocationSetById.pending, (state, action) => {
      state.organizations.rest.getLocationSetStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(getLocationSetById.fulfilled, (state, action) => {
      state.organizations.rest.getLocationSetStatus = HttpFetchStatus.Success;
      state.organizations.locationSet = action.payload ?? null;
    });
    builder.addCase(getLocationSetById.rejected, (state, action) => {
      state.organizations.rest.getLocationSetStatus = HttpFetchStatus.Failed;
      state.organizations.rest.getLocationSetError = action.payload ?? null;
    });
    builder.addCase(getOrganizationDeleteDataById.pending, (state, action) => {
      state.organizations.rest.getDeleteStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(getOrganizationDeleteDataById.fulfilled, (state, action) => {
      state.organizations.rest.getDeleteStatus = HttpFetchStatus.Success;
      state.organizations.deleteData = action.payload ?? null;
    });
    builder.addCase(getOrganizationDeleteDataById.rejected, (state, action) => {
      state.organizations.rest.getDeleteStatus = HttpFetchStatus.Failed;
      state.organizations.rest.getDeleteError = action.payload ?? null;
    });
    builder.addCase(getOrganizationUsersById.pending, (state, action) => {
      state.users.rest.getStatus = HttpFetchStatus.Loading;
      state.users.data = null;
    });
    builder.addCase(getOrganizationUsersById.fulfilled, (state, action) => {
      state.users.rest.getStatus = HttpFetchStatus.Success;
      state.users.data = action.payload ?? null;
    });
    builder.addCase(getOrganizationUsersById.rejected, (state, action) => {
      state.users.rest.getStatus = HttpFetchStatus.Failed;
      state.users.rest.getError = action.payload ?? null;
    });
    builder.addCase(createSubOrganization.pending, (state, action) => {
      state.organizations.rest.postStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(createSubOrganization.fulfilled, (state, action) => {
      state.organizations.rest.postStatus = HttpFetchStatus.Success;
      if (state.organizations.sub != null) {
        state.organizations.sub = updateDataList(action.payload, state.organizations.sub);
      }
      if (action.payload.role === OrganizationRole.LOCATION) {
        state.organizations.locationSet?.push(action.payload);
      }
    });
    builder.addCase(createSubOrganization.rejected, (state, action) => {
      state.organizations.rest.postStatus = HttpFetchStatus.Failed;
      state.organizations.rest.postError = action.payload ?? null;
    });
    builder.addCase(createUser.pending, (state, action) => {
      state.users.rest.postStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(createUser.fulfilled, (state, action) => {
      state.users.rest.postStatus = HttpFetchStatus.Success;
      if (state.users.data != null) {
        state.users.data = updateDataList(action.payload, state.users.data);
      }
    });
    builder.addCase(createUser.rejected, (state, action) => {
      state.users.rest.postStatus = HttpFetchStatus.Failed;
      state.users.rest.postError = action.payload ?? null;
    });
    builder.addCase(patchUserById.pending, (state, action) => {
      state.users.rest.patchStatus = HttpFetchStatus.Loading;
      state.users.rest.patchId = action.meta.arg.id;
      state.users.rest.patchType = action.meta.arg.patchType;
    });
    builder.addCase(patchUserById.fulfilled, (state, action) => {
      state.users.rest.patchStatus = HttpFetchStatus.Success;

      if (state.users.data != null) {
        const existing_user = state.users.data.find((user) => user.id === action.payload.id);
        const existing_user_index = state.users.data.findIndex(
          (user) => user.id === action.payload.id,
        );

        if (existing_user != null && existing_user_index !== -1) {
          if (existing_user.organization.id !== action.payload.organization.id) {
            // user has been moved to different organization
            state.users.data.splice(existing_user_index, 1);
            state.selectedUserId = null;
          } else {
            state.users.data = updateDataList(action.payload, state.users.data);

            // For a change of owner patch only updates the new owner instance, so self update the old one
            const oldOwner = state.users.data.find(
              (user) => user.id !== action.payload.id && user.role === UserRole.OWNER,
            );
            if (oldOwner != null) {
              oldOwner.role = UserRole.ADMIN;
            }
          }
        }
      }
    });
    builder.addCase(patchUserById.rejected, (state, action) => {
      state.users.rest.patchStatus = HttpFetchStatus.Failed;
      state.users.rest.patchError = action.payload ?? null;
      state.users.rest.patchType = null;
    });
    builder.addCase(deleteUserById.pending, (state, action) => {
      state.users.rest.deleteStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(deleteUserById.fulfilled, (state, action) => {
      if (action.meta.arg.deletingSelf) {
        removeToken();
        return { ...initialOrganizationState };
      }

      state.users.rest.deleteStatus = HttpFetchStatus.Success;
      if (state.users.data != null) {
        const deletedUser = state.users.data.find((user) => user.id === action.payload);
        const userIndex = state.users.data.findIndex((user) => user.id === action.payload);
        if (userIndex !== -1 && deletedUser != null) {
          state.users.data.splice(userIndex, 1);
          state.selectedUserId = null;
        }
      }
    });
    builder.addCase(deleteUserById.rejected, (state, action) => {
      state.users.rest.deleteStatus = HttpFetchStatus.Failed;
      state.users.rest.deleteError = action.payload ?? null;
    });
    builder.addCase(getUserById.pending, (state, action) => {
      state.users.rest.getByIdStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(getUserById.fulfilled, (state, action) => {
      state.users.rest.getByIdStatus = HttpFetchStatus.Success;
      state.users.rest.getByIdError = null;
      state.users.current = action.payload;
    });
    builder.addCase(getUserById.rejected, (state, action) => {
      state.users.rest.getByIdStatus = HttpFetchStatus.Failed;
      state.users.rest.getByIdError = action.payload ?? null;
    });

    builder.addCase(getAllUserDataById.pending, (state, action) => {
      state.users.rest.getAllUserDataByIdStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(getAllUserDataById.fulfilled, (state, action) => {
      state.users.rest.getAllUserDataByIdStatus = HttpFetchStatus.Success;
      state.users.rest.getAllUserDataByIdError = null;
      state.users.currentUserAllData = action.payload;
    });
    builder.addCase(getAllUserDataById.rejected, (state, action) => {
      state.users.rest.getAllUserDataByIdStatus = HttpFetchStatus.Failed;
      state.users.rest.getAllUserDataByIdError = action.payload ?? null;
    });

    builder.addCase(patchOrganizationById.pending, (state, action) => {
      state.organizations.rest.patchStatus = HttpFetchStatus.Loading;
      state.organizations.rest.patchId = action.meta.arg.id;
    });
    builder.addCase(patchOrganizationById.fulfilled, (state, action) => {
      state.organizations.rest.patchStatus = HttpFetchStatus.Success;
      state.organizations.current = action.payload;
      if (action.meta.arg.data.parentId != null || action.meta.arg.data.role) {
        state.organizations.rest.getLocationSetStatus = HttpFetchStatus.Idle;
      }
    });
    builder.addCase(patchOrganizationById.rejected, (state, action) => {
      state.organizations.rest.patchStatus = HttpFetchStatus.Failed;
      state.organizations.rest.patchError = action.payload ?? null;
    });

    builder.addCase(deleteOrganizationById.pending, (state, action) => {
      state.organizations.rest.deleteStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(deleteOrganizationById.fulfilled, (state, action) => {
      state.organizations.rest.deleteStatus = HttpFetchStatus.Success;
      const deletedIndex = state.organizations.sub?.findIndex((org) => org.id === action.payload);
      if (state.organizations.sub != null && deletedIndex != null && deletedIndex !== -1) {
        state.organizations.sub.splice(deletedIndex, 1);
      }
      const locationIndex = state.organizations.locationSet?.findIndex(
        (location) => location.id === action.meta.arg,
      );
      if (locationIndex !== -1 && locationIndex != null) {
        state.organizations.locationSet?.splice(locationIndex, 1);
      }
    });
    builder.addCase(deleteOrganizationById.rejected, (state, action) => {
      state.organizations.rest.deleteStatus = HttpFetchStatus.Failed;
      state.organizations.rest.deleteError = action.payload ?? null;
    });
    builder.addCase(getDeploymentWeeksById.pending, (state, action) => {
      state.organizations.rest.getDeploymentWeeksStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(getDeploymentWeeksById.fulfilled, (state, action) => {
      if (action.meta.arg.id === state.organizations.current?.id) {
        state.organizations.rest.getDeploymentWeeksStatus = HttpFetchStatus.Success;
        state.organizations.deploymentWeeks = action.payload;
      }
    });
    builder.addCase(getDeploymentWeeksById.rejected, (state, action) => {
      state.organizations.rest.getDeploymentWeeksStatus = HttpFetchStatus.Failed;
      state.organizations.rest.getDeploymentWeeksError = action.payload ?? null;
    });

    builder.addCase(getSearchResults.pending, (state, action) => {
      state.search.getStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(getSearchResults.fulfilled, (state, action) => {
      state.search.getStatus = HttpFetchStatus.Success;
      state.search.data = action.payload.data;
      state.search.count = action.payload.count;
    });
    builder.addCase(getSearchResults.rejected, (state, action) => {
      state.search.getStatus = HttpFetchStatus.Failed;
      state.search.getError = action.payload ?? null;
    });

    builder.addCase(getApiKeysByOrganizationId.pending, (state, action) => {
      state.externalApi.rest.getStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(getApiKeysByOrganizationId.fulfilled, (state, action) => {
      state.externalApi.rest.getStatus = HttpFetchStatus.Success;
      state.externalApi.data = action.payload;
    });
    builder.addCase(getApiKeysByOrganizationId.rejected, (state, action) => {
      state.externalApi.rest.getStatus = HttpFetchStatus.Failed;
      state.externalApi.rest.getError = action.payload ?? null;
    });
    builder.addCase(createApiKey.pending, (state, action) => {
      state.externalApi.rest.postStatus = HttpFetchStatus.Loading;
      state.externalApi.created = null;
    });
    builder.addCase(createApiKey.fulfilled, (state, action) => {
      state.externalApi.rest.postStatus = HttpFetchStatus.Success;
      const { apiKey, ...apiKeyMetadata } = action.payload;
      state.externalApi.data = updateDataList(apiKeyMetadata, state.externalApi.data);
      // Held only until the dialog closes - resetExternalApiRestState clears it.
      state.externalApi.created = action.payload;
    });
    builder.addCase(createApiKey.rejected, (state, action) => {
      state.externalApi.rest.postStatus = HttpFetchStatus.Failed;
      state.externalApi.rest.postError = action.payload ?? null;
    });
    builder.addCase(deleteApiKeyById.pending, (state, action) => {
      state.externalApi.rest.deleteStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(deleteApiKeyById.fulfilled, (state, action) => {
      state.externalApi.rest.deleteStatus = HttpFetchStatus.Success;
      if (state.externalApi.data != null) {
        const apiKeyIndex = state.externalApi.data.findIndex((key) => key.id === action.payload);
        if (apiKeyIndex !== -1) {
          state.externalApi.data.splice(apiKeyIndex, 1);
        }
      }
    });
    builder.addCase(deleteApiKeyById.rejected, (state, action) => {
      state.externalApi.rest.deleteStatus = HttpFetchStatus.Failed;
      state.externalApi.rest.deleteError = action.payload ?? null;
    });
  },
});

export const {
  resetOrganizationState,
  resetOrganizationsRestState,
  resetUsersRestState,
  resetSearchRestStatus,
  resetExternalApiRestState,
  clearCreatedApiKey,
  setSelectedUserId,
  setOrganizationWebSocketStatus,
} = organizationSlice.actions;

export default organizationSlice.reducer;
